import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import api, { apiError } from "../lib/api";

export default function CoverLetterFill() {
  const { id, resource } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [genBusy, setGenBusy] = useState(false);

  const loadForm = useCallback(() => {
    setLoading(true);
    api.get(`/contracts/cover-letter/form?record_id=${id}`).then((r) => {
      setData(r.data);
      const init = {};
      (r.data.fields || []).forEach((f) => { init[f.id] = f.value || ""; });
      setValues(init);
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { loadForm(); }, [loadForm]);

  const setVal = (fid, v) => setValues((s) => ({ ...s, [fid]: v }));

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data: res } = await api.post("/contracts/cover-letter/generate", { record_id: id, sections: values, name: data.name });
      const dl = await api.get(`/documents/${res.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(dl.data);
      const a = document.createElement("a"); a.href = url; a.download = `${data.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Cover letter saved to Documents & downloaded");
    } catch (e) { toast.error(apiError(e)); } finally { setGenBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="cover-letter-page">
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur px-4 sm:px-8 py-3">
        <div className="max-w-[820px] mx-auto flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/record/${resource}/${id}`)} data-testid="cl-back">
            <ArrowLeft className="h-4 w-4 mr-1.5" />Back
          </Button>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Mail className="h-4 w-4 text-primary" />{data?.name || "Cover Letter"}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" onClick={generate} disabled={genBusy || loading || !data} data-testid="cl-generate">
              {genBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}Generate & Save PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !data ? (
          <div className="text-center py-24 text-muted-foreground">Could not load the cover letter.</div>
        ) : (
          <div className="max-w-[820px] mx-auto space-y-4" data-testid="cover-letter-body">
            <p className="text-xs text-muted-foreground">Set the recipient name and footer phone, then Generate & Save. The rest of the letter matches the approved Arqitek design.</p>
            {(data.fields || []).map((f) => (
              <div key={f.id} className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{f.label}</label>
                {f.multiline ? (
                  <textarea
                    value={values[f.id] || ""}
                    onChange={(e) => setVal(f.id, e.target.value)}
                    rows={Math.max(3, Math.ceil((values[f.id]?.length || 0) / 90))}
                    data-testid={`cl-field-${f.id}`}
                    className="w-full rounded-md border border-border bg-secondary/30 px-3 py-2 text-sm text-foreground/90 leading-6 outline-none focus:border-primary resize-y"
                  />
                ) : (
                  <input
                    value={values[f.id] || ""}
                    onChange={(e) => setVal(f.id, e.target.value)}
                    data-testid={`cl-field-${f.id}`}
                    className="w-full rounded-md border border-border bg-secondary/30 px-3 py-2 text-sm font-medium text-foreground outline-none focus:border-primary"
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
